import React, { useEffect, useState } from "react";
import { CheckCircle2, PencilLine, Loader2, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { ApprovalPanel } from "./workflow/ApprovalPanel";
import { MediaMarkdown } from "./MediaMarkdown";
import { useStepOutput } from "../hooks/useStepOutput";

// Chat card for a stage that finished and waits for approval: the result excerpt + the SAME Setujui/Revisi controls as Ruang Kerja.
export function StageResultCard({ m, refreshKey }) {
  const ev = m.task_event;
  const [task, setTask] = useState(null);
  const [open, setOpen] = useState(false);
  const load = async () => { try { const r = await api.get(`/tasks/${ev.task_id}`); setTask(r.data); } catch { /* the chip stays usable */ } };
  useEffect(() => { load(); }, [ev.task_id, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const nav = useNavigate();
  const idx = (ev.stage_n || 1) - 1;
  const step = task?.steps?.[idx];
  const { output: out, snippet, hasOutput, loading } = useStepOutput(ev.task_id, step, open);
  if (!ev || ev.kind !== "stage_waiting_approval") return null;
  const waiting = step?.status === "waiting_approval";
  return (
    <div className="mt-2 w-full max-w-xl rounded-2xl border border-amber-200 bg-white p-4 shadow-sm" data-testid={`stage-result-card-${idx}`}>
      <p className="text-[11px] font-bold uppercase tracking-wider text-amber-700">Hasil tahap {idx + 1} · menunggu persetujuan</p>
      <p className="mt-0.5 text-sm font-bold text-slate-900" data-no-i18n data-testid="stage-result-title">{step?.title || ev.stage_title}</p>
      {step?.persona_name && <p className="text-xs text-slate-500">oleh {step.persona_name}{step.credits ? ` · ${Number(step.credits).toFixed(1)} kredit` : ""}</p>}
      {!task && <p className="mt-2 flex items-center gap-2 text-xs text-slate-400"><Loader2 size={12} className="animate-spin" /> Memuat hasil…</p>}
      {step && (hasOutput || step.output) && (
        <div className="mt-2">
          <button type="button" onClick={() => setOpen((v) => !v)} className="flex items-center gap-1 text-xs font-semibold text-[#2F6BFF]" data-testid="stage-result-toggle">
            {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />} {open ? "Sembunyikan hasil" : "Lihat hasil lengkap"}
          </button>
          {!open && <p className="mt-1 line-clamp-3 text-xs text-slate-600" data-no-i18n>{snippet || (step.output || "").slice(0, 300)}</p>}
          {open && (out || step.output) && <div className="mt-2 max-h-[60vh] overflow-y-auto rounded-xl bg-slate-50 p-3 text-sm text-slate-700" data-testid="stage-result-output"><MediaMarkdown content={out || step.output} media={step.media || []} taskId={ev.task_id} /></div>}
          {open && !out && !step.output && loading && <p className="mt-2 text-xs text-slate-400">Memuat…</p>}
        </div>
      )}
      {task && step && waiting && <ApprovalPanel task={task} step={step} onDone={load} />}
      {task && step && !waiting && (
        <p className="mt-3 flex items-center gap-1.5 rounded-xl bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600" data-testid="stage-result-settled">
          {step.status === "completed" ? <CheckCircle2 size={14} className="text-emerald-600" /> : <PencilLine size={14} className="text-amber-600" />}
          {step.status === "completed" ? "Tahap ini sudah disetujui." : step.status === "running" || step.status === "queued" ? "Tahap ini sedang direvisi…" : `Status tahap: ${step.status}`}
        </p>
      )}
      <button type="button" onClick={() => nav(`/workspace/${ev.task_id}`)} className="mt-2 flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-[#2F6BFF]" data-testid="stage-result-open-workspace"><ExternalLink size={11} /> Buka di Ruang Kerja</button>
    </div>
  );
}
